/* The filter snippet loads the exact Shopify-versioned filter asset before this
   controller. The filter module exposes its resolver on window so this controller
   never follows a stale relative-import URL from Shopify's CDN. */
var ncoAuthoritativeResolver = function (product, filters) {
  if (typeof window !== 'undefined' && typeof window.ncoResolveFilteredVariant === 'function') {
    return window.ncoResolveFilteredVariant(product, filters);
  }
  return { matched: false };
};

/*
 * nco-collection.js
 *
 * Collection page controller — client-side sort, mobile filter overlay
 * open/close with scroll lock, grid card visibility driven by
 * nco:filter:change events from the 4.2 filter engine, and SEO
 * rel-prev/next injection.
 *
 * Standards:
 *   E5  — zero ESLint errors/warnings
 *   E7  — loaded as type="module" defer, never blocks render
 *   S2  — vanilla JS, event delegation, no jQuery
 *   S5  — ARIA state, keyboard (Escape), focus management
 *   S6  — deferred, no render-blocking
 */

/* ------------------------------------------------------------------ */
/* Selectors                                                           */
/* ------------------------------------------------------------------ */

const SEL = {
  collection: '[data-nco-collection]',
  sidebar: '[data-nco-sidebar]',
  filterToggle: '[data-nco-filter-toggle]',
  filterClose: '[data-nco-filter-overlay-close]',
  filterApply: '[data-nco-filter-apply]',
  backdrop: '[data-nco-backdrop]',
  grid: '[data-nco-grid]',
  card: '[data-nco-family-id]',
  sort: '[data-nco-sort]',
  visibleCount: '[data-nco-visible-count]',
  overlayCount: '[data-nco-overlay-count]',
  cta: '[data-nco-cta]',
  prevLink: '.nco-collection__page-link--prev',
  nextLink: '.nco-collection__page-link--next',
  pagination: '[data-nco-pagination]',
  pagePrev: '[data-nco-page-prev]',
  pageNext: '[data-nco-page-next]',
  pageInfo: '[data-nco-page-info]',
};

const CSS_OPEN = 'nco-collection__sidebar--open';
const CSS_BACKDROP = 'nco-collection__backdrop--visible';

/* ------------------------------------------------------------------ */
/* State                                                               */
/* ------------------------------------------------------------------ */

let currentSort = 'featured';
let currentPage = 1;
let perPage = 24;
let visibleFamilyIds = [];
var tileEligibility = {};

/* ------------------------------------------------------------------ */
/* Mobile filter overlay                                               */
/* ------------------------------------------------------------------ */

function openOverlay() {
  const sidebar = document.querySelector(SEL.sidebar);
  const backdrop = document.querySelector(SEL.backdrop);
  if (!sidebar) return;

  sidebar.classList.add(CSS_OPEN);
  if (backdrop) backdrop.classList.add(CSS_BACKDROP);
  document.body.style.overflow = 'hidden';

  const closeBtn = sidebar.querySelector(SEL.filterClose);
  if (closeBtn) closeBtn.focus();
}

function closeOverlay() {
  const sidebar = document.querySelector(SEL.sidebar);
  const backdrop = document.querySelector(SEL.backdrop);
  if (!sidebar) return;

  sidebar.classList.remove(CSS_OPEN);
  if (backdrop) backdrop.classList.remove(CSS_BACKDROP);
  document.body.style.overflow = '';

  const toggle = document.querySelector(SEL.filterToggle);
  if (toggle) toggle.focus();
}

/* ------------------------------------------------------------------ */
/* Sort                                                                */
/* ------------------------------------------------------------------ */

function sortComparator(a, b) {
  switch (currentSort) {
    case 'price-asc':
      return Number(a.dataset.ncoCardPrice) - Number(b.dataset.ncoCardPrice);
    case 'price-desc':
      return Number(b.dataset.ncoCardPrice) - Number(a.dataset.ncoCardPrice);
    case 'newest':
      return Number(b.dataset.ncoCardDate) - Number(a.dataset.ncoCardDate);
    default:
      return Number(a.dataset.ncoCardOrder) - Number(b.dataset.ncoCardOrder);
  }
}

function sortCardArray(cards) {
  cards.sort(sortComparator);
}

function sortCards() {
  const grid = document.querySelector(SEL.grid);
  if (!grid) return;

  const cards = Array.from(grid.querySelectorAll(SEL.card));
  const cta = grid.querySelector(SEL.cta);

  cards.sort(sortComparator);

  if (cta) cta.remove();

  for (let i = 0; i < cards.length; i++) {
    grid.appendChild(cards[i]);
  }

  if (cta) {
    const cols = getComputedStyle(grid).getPropertyValue('grid-template-columns');
    const colCount = cols ? cols.split(' ').length : 3;
    const visibleCards = cards.filter(function (c) { return !c.hidden; });
    const insertAfter = Math.min(colCount, visibleCards.length);
    const refCard = visibleCards[insertAfter - 1];
    if (refCard && refCard.nextSibling) {
      grid.insertBefore(cta, refCard.nextSibling);
    } else {
      grid.appendChild(cta);
    }
  }
}

/* ------------------------------------------------------------------ */
/* Grid visibility from filter events                                  */
/* ------------------------------------------------------------------ */

function updateGridVisibility(familyIds, detail) {
  visibleFamilyIds = familyIds;
  lastActiveFilters = (detail && detail.activeFilters) || {};
  currentPage = 1;

  if (detail) {
    updateCardContent(detail);
  }

  applyPagination();
}

/* ------------------------------------------------------------------ */
/* Card content updates from filter resolution                         */
/* ------------------------------------------------------------------ */

/* Market-aware whole-unit price. The filters shell emits `{{ 123456 | money }}` rendered in the
   shopper's presentment currency (data-nco-money-sample); the symbol placement and group
   separator are lifted from that sample instead of hardcoding "$" + en-AU. Falls back to the
   old AUD formatting only when the sample is missing. */
var ncoMoneySample = (function () {
  var el = document.querySelector('[data-nco-money-sample]');
  var raw = el ? (el.getAttribute('data-nco-money-sample') || '') : '';
  // Money formats may carry entity symbols (&euro;, &pound;, &#8364;) — decode them once.
  if (raw.indexOf('&') !== -1) {
    var ta = document.createElement('textarea');
    ta.innerHTML = raw;
    raw = ta.value;
  }
  return raw;
})();
function formatCardPrice(cents) {
  var m = /1([.,\u00a0\s]?)234(?:([.,])56)?/.exec(ncoMoneySample);
  var hasCents = cents % 100 !== 0;
  if (!m) {
    // No usable sample: never invent a symbol — fall back to the shop's own formatting rules.
    return (window.Shopify && window.Shopify.formatMoney)
      ? window.Shopify.formatMoney(cents)
      : (cents / 100).toLocaleString(undefined, { minimumFractionDigits: hasCents ? 2 : 0, maximumFractionDigits: 2 });
  }
  var whole = hasCents ? Math.floor(cents / 100) : Math.round(cents / 100);
  var digits = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, m[1] || '');
  // Keep the cents only when the amount has them AND the sample shows a decimal part.
  if (hasCents && m[2]) digits += m[2] + String(cents % 100).padStart(2, '0');
  return ncoMoneySample.replace(m[0], digits);
}

/* A curated split tile pins an identity (for example Hoop / Raw Textured), while a
   collection filter can add a different axis (for example Mount=Wall).  The filter
   resolver's representative combo is deliberately finish-agnostic when finish is
   not selected, so keep the tile's finish but replace/add it to the combo signature
   before looking up the image and exact variant. */
function ncoCanonicalPinnedFinish(value) {
  var finish = String(value || '').trim();
  var lower = finish.toLowerCase();
  if (lower === 'raw') return 'Raw Textured';
  if (lower === 'signature') return 'Signature Smooth';
  return finish;
}

function ncoPinnedComboSignature(signature, pinFinish) {
  var finish = ncoCanonicalPinnedFinish(pinFinish);
  if (!finish) return signature || '';

  var parts = String(signature || '').split('|').filter(Boolean);
  var replaced = false;
  for (var i = 0; i < parts.length; i++) {
    if (parts[i].indexOf('finish=') === 0) {
      parts[i] = 'finish=' + finish;
      replaced = true;
    }
  }
  if (!replaced) parts.push('finish=' + finish);
  return parts.join('|');
}

function ncoColourHandle(value) {
  return String(value || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function updateCardContent(detail) {
  var resolved = detail.resolved || {};
  var filters = detail.activeFilters || {};
  var products = detail.products || [];
  var hasActiveFilters = Object.keys(filters).some(function (k) {
    return filters[k] && filters[k].length > 0;
  });

  var singleColour = (filters.colour && filters.colour.length === 1) ? filters.colour[0] : null;
  var singleColourHandle = singleColour
    ? singleColour.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    : null;

  var productMap = {};
  for (var p = 0; p < products.length; p++) {
    productMap[products[p].id] = products[p];
  }

  /* The filter event may carry a reduced product projection. Keep the complete
     server-rendered index available as the authoritative fallback for high-variant
     metadata (variantIndex, configurationImageMap, and exact media maps). */
  var fullIndexMap = {};
  var indexEl = document.getElementById('nco-product-index');
  if (indexEl) {
    try {
      var fullIndex = JSON.parse(indexEl.textContent);
      if (!Array.isArray(fullIndex)) fullIndex = Object.keys(fullIndex || {}).map(function (key) { return fullIndex[key]; });
      for (var fi = 0; fi < fullIndex.length; fi++) {
        if (fullIndex[fi] && fullIndex[fi].id) fullIndexMap[fullIndex[fi].id] = fullIndex[fi];
      }
    } catch (e) {
      fullIndexMap = {};
    }
  }

  tileEligibility = {};

  var cards = document.querySelectorAll('[data-nco-product-card]');
  for (var i = 0; i < cards.length; i++) {
    var card = cards[i];
    var wrapper = card.closest('[data-nco-family-id]');
    var familyId = wrapper ? wrapper.getAttribute('data-nco-family-id') : '';
    var cardProductId = card.getAttribute('data-product-id');
    var productId = familyId || cardProductId;
    var handle = card.getAttribute('data-nco-handle');
    var defaultUrl = card.getAttribute('data-nco-default-url') || ('/products/' + handle);
    var defaultPrice = Number(card.getAttribute('data-nco-default-price') || 0);

    var links = card.querySelectorAll('[data-nco-card-link]');
    var priceEl = card.querySelector('[data-nco-card-price-text]');
    var imgEl = card.querySelector('[data-nco-card-image]');

    var res = resolved[productId] || resolved[cardProductId];
    var productIndexEntry = fullIndexMap[productId] || fullIndexMap[cardProductId] ||
      productMap[productId] || productMap[cardProductId];

    var pinF = wrapper ? wrapper.getAttribute('data-nco-pin-finish') : null;
    var pinT = wrapper ? wrapper.getAttribute('data-nco-pin-tray') : null;
    if (res && productIndexEntry && hasActiveFilters && (pinF || pinT)) {
      /* A split tile is a constrained product, not a cosmetic label. Apply its
         pinned finish/tray before resolving price, eligibility, variant URL and
         image so a Raw tile cannot inherit Signature pricing or a Surface-only
         match under the Wall filter. */
      var tileFilters = {};
      var filterKeys = Object.keys(filters);
      for (var tf = 0; tf < filterKeys.length; tf++) {
        tileFilters[filterKeys[tf]] = (filters[filterKeys[tf]] || []).slice();
      }
      if (pinF) tileFilters.finish = [ncoCanonicalPinnedFinish(pinF)];
      if (pinT) tileFilters.tray = [pinT];
      var tileRes = ncoAuthoritativeResolver(productIndexEntry, tileFilters);
      var tileId = tileKey(wrapper);
      tileEligibility[tileId] = tileRes.matched !== false;
      if (!tileRes.matched) continue;
      res = tileRes;
    }

    /* High-variant products carry their uncapped variant index in the product
       entry. Re-resolve those families at card-render time so the exact variant
       join is authoritative for the link, price, and configuration image even
       when a stale/cached family-level filter event arrived first. */
    if (productIndexEntry && hasActiveFilters && productIndexEntry.variantIndex && !(pinF || pinT)) {
      var indexRes = ncoAuthoritativeResolver(productIndexEntry, filters);
      if (indexRes && indexRes.matched) res = indexRes;
    }

    if (res && hasActiveFilters) {
      // Update price
      if (priceEl && !priceEl.hasAttribute('data-nco-price-locked')) {
        var priceText = res.isRange
          ? 'From ' + formatCardPrice(res.price)
          : formatCardPrice(res.price);
        priceEl.textContent = priceText;
      }

      // Update card sort attribute on wrapper (cents) — except locked bundle prices, whose
      // sort value must stay the displayed From-price, not the resolved unit price.
      if (wrapper && !(priceEl && priceEl.hasAttribute('data-nco-price-locked'))) {
        wrapper.setAttribute('data-nco-card-price', String(res.price));
      }

      // Update links. An exact variant URL already contains every active option. A curated
      // split tile needs one extra join when the active filters do not include its pinned
      // finish; otherwise both finish siblings can silently land on the same variant.
      var pinnedUrl = res.url;
      if (wrapper && pinnedUrl) {
        if (!res.exactVariant) {
          if (pinF && pinnedUrl.indexOf('finish=') === -1) pinnedUrl += (pinnedUrl.indexOf('?') === -1 ? '?' : '&') + 'finish=' + encodeURIComponent(pinF);
          if (pinT && pinnedUrl.indexOf('tray=') === -1) pinnedUrl += (pinnedUrl.indexOf('?') === -1 ? '?' : '&') + 'tray=' + encodeURIComponent(pinT);
        }
      }
      for (var l = 0; l < links.length; l++) {
        links[l].href = pinnedUrl;
      }
    } else {
      // Revert to defaults
      if (priceEl && defaultPrice && !priceEl.hasAttribute('data-nco-price-locked')) {
        priceEl.textContent = formatCardPrice(defaultPrice);
      }
      for (var l2 = 0; l2 < links.length; l2++) {
        links[l2].href = defaultUrl;
      }
      if (wrapper && defaultPrice && !(priceEl && priceEl.hasAttribute('data-nco-price-locked'))) {
        wrapper.setAttribute('data-nco-card-price', String(defaultPrice));
      }
    }

    if (imgEl) {
      var defaultImg = imgEl.getAttribute('data-nco-default-image');
      var defaultAlt = imgEl.getAttribute('data-nco-default-alt');

      /* Native (<=250 variant) MOUNT-AWARE swap.
         colourImageMap is keyed by colour only and holds the first (Surface) variant per colour, so a
         Mount=Wall filter could never change the card image. When a NON-COLOUR matrix filter is active
         (mount/size/finish/tone/tray), resolve the image from variantComboImageMap using the SAME
         representative combo that drives the card's price and link. Colour-only filtering is left on
         the existing path so today's behaviour is unchanged. Fails CLOSED to the default image. */
      var nonColourActive = ['mountType', 'overflow', 'tapHole', 'tray', 'priceBand', 'finish']
        .some(function (k) { return filters[k] && filters[k].length > 0; });
      var vcm = productIndexEntry ? productIndexEntry.variantComboImageMap : null;
      var vcSig = res && res.cardComboSig;
      var resolvedColour = res && res.selectedOptions && res.selectedOptions.colour;
      var resolvedColourHandle = singleColourHandle || ncoColourHandle(resolvedColour);
      /* F3 (staff feedback): finish-matched card image from the render-only finishImageMap
         ("<Finish>|<colour-handle>" -> variant featured image). Used when a single finish is
         selected: preferred colour key first, else any entry for that finish. Fails CLOSED. */
      var fim = productIndexEntry ? productIndexEntry.finishImageMap : null;
      var finishSel = (filters.finish && filters.finish.length === 1) ? filters.finish[0] : null;
      var fimUrl = null;
      if (fim && finishSel) {
        if (singleColourHandle) fimUrl = fim[finishSel + '|' + singleColourHandle] || null;
        if (!fimUrl) {
          var fimPrefix = finishSel + '|';
          for (var fimKey in fim) { if (fimKey.indexOf(fimPrefix) === 0) { fimUrl = fim[fimKey]; break; } }
        }
      }
      /* Split-card identity: a finish-pinned card never takes a finish-agnostic swap — under a
         colour-only filter both split siblings would otherwise receive the same (usually
         Signature) colour render. Resolve the pin's own finish|colour render when exactly one
         colour is selected; otherwise keep/revert to the server-rendered pin image. Fails CLOSED
         to the default image. */
      var pinFin = wrapper ? wrapper.getAttribute('data-nco-pin-finish') : null;
      if (pinFin) {
        var pinImgUrl = null;
        if (vcm && vcSig && nonColourActive && resolvedColourHandle) {
          var pinSig = ncoPinnedComboSignature(vcSig, pinFin);
          pinImgUrl = vcm[pinSig + '~' + resolvedColourHandle] || null;
        }
        if (!pinImgUrl && fim && hasActiveFilters && singleColourHandle) {
          pinImgUrl = fim[ncoCanonicalPinnedFinish(pinFin) + '|' + singleColourHandle] || null;
        }
        if (pinImgUrl) {
          imgEl.src = pinImgUrl + (pinImgUrl.indexOf('?') >= 0 ? '&' : '?') + 'width=800';
          imgEl.removeAttribute('srcset');
          imgEl.setAttribute('data-nco-image-swapped', '1');
        } else if (imgEl.hasAttribute('data-nco-image-swapped') && defaultImg) {
          imgEl.src = defaultImg;
          imgEl.removeAttribute('srcset');
          if (defaultAlt) imgEl.alt = defaultAlt;
          imgEl.removeAttribute('data-nco-image-swapped');
        }
      } else if (vcm && vcSig && nonColourActive) {
        var repColour = singleColourHandle;
        if (!repColour) {
          var reps = productIndexEntry.variantComboRep || {};
          repColour = reps[vcSig] || null;           // deterministic representative, not "first variant"
        }
        var vcUrl = repColour ? vcm[vcSig + '~' + repColour] : null;   // exact key, never substring
        if (!vcUrl && fimUrl) vcUrl = fimUrl;                          // F3: finish render as fallback
        if (vcUrl) {
          /* emitted without the ?v= cache-buster to keep the index small; add the card width back */
          imgEl.src = vcUrl + (vcUrl.indexOf('?') >= 0 ? '&' : '?') + 'width=800';
          imgEl.removeAttribute('srcset');
          imgEl.alt = (card.getAttribute('data-product-title') || '') + (singleColour ? ' - ' + singleColour : '');
          imgEl.setAttribute('data-nco-image-swapped', '1');
        } else if (imgEl.hasAttribute('data-nco-image-swapped') && defaultImg) {
          imgEl.src = defaultImg;
          imgEl.removeAttribute('srcset');
          if (defaultAlt) imgEl.alt = defaultAlt;
          imgEl.removeAttribute('data-nco-image-swapped');
        }
      } else {
        /* Configuration imagery is more specific than a finish-only image. A finish map
           cannot prove the selected mount/overflow/tap-hole, so it must never win over an
           exact Shelf/Loop configuration image. */
        var cfgMap = productIndexEntry ? productIndexEntry.configurationImageMap : null;
        var cfgColourHandle = resolvedColourHandle;
        var cfgByColour = cfgMap && cfgMap.images && cfgMap.images[cfgColourHandle];
        var cfgKey = res && res.cardConfigKey;
        var cfgUrl = (cfgByColour && cfgKey && cfgByColour[cfgKey]) || null;
        if (cfgUrl && (singleColourHandle || nonColourActive)) {
          var cfgSep = cfgUrl.indexOf('?') >= 0 ? '&' : '?';
          imgEl.src = cfgUrl + cfgSep + 'width=800';
          imgEl.removeAttribute('srcset');
          imgEl.alt = (card.getAttribute('data-product-title') || '') + (resolvedColour ? ' - ' + resolvedColour : '');
          imgEl.setAttribute('data-nco-image-swapped', '1');
        } else if (fimUrl && nonColourActive && !cfgMap) {
          /* F3: no exact configuration image — swap from the finish map only when
             the product has no configuration map answer. */
          imgEl.src = fimUrl + (fimUrl.indexOf('?') >= 0 ? '&' : '?') + 'width=800';
          imgEl.removeAttribute('srcset');
          imgEl.setAttribute('data-nco-image-swapped', '1');
        } else if (singleColourHandle && !cfgMap) {
          // Products WITHOUT a configuration map (cylinders / native <=250) use the legacy colour map.
          var cim = productIndexEntry ? productIndexEntry.colourImageMap : null;
          if (cim && cim[singleColourHandle] && cim[singleColourHandle].src) {
            imgEl.src = cim[singleColourHandle].src;
            imgEl.removeAttribute('srcset');
            if (cim[singleColourHandle].alt) imgEl.alt = cim[singleColourHandle].alt;
            imgEl.setAttribute('data-nco-image-swapped', '1');
          } else if (imgEl.hasAttribute('data-nco-image-swapped') && defaultImg) {
            imgEl.src = defaultImg;
            imgEl.removeAttribute('srcset');
            if (defaultAlt) imgEl.alt = defaultAlt;
            imgEl.removeAttribute('data-nco-image-swapped');
          }
        } else if (imgEl.hasAttribute('data-nco-image-swapped') && defaultImg) {
          imgEl.src = defaultImg;
          imgEl.removeAttribute('srcset');
          if (defaultAlt) imgEl.alt = defaultAlt;
          imgEl.removeAttribute('data-nco-image-swapped');
        }
      }
    }
  }
}

/* ------------------------------------------------------------------ */
/* Client-side pagination                                              */
/* ------------------------------------------------------------------ */

var lastActiveFilters = {};
function tileKey(card) { return card.getAttribute('data-nco-tile-id') || card.getAttribute('data-nco-family-id'); }
function normFinish(v) { var k = String(v || '').trim().toLowerCase(); return { 'raw': 'Raw Textured', 'raw textured': 'Raw Textured', 'signature': 'Signature Smooth', 'signature smooth': 'Signature Smooth' }[k] || String(v || '').trim(); }
function tilePinsMatch(card, filters) {
  var pf = card.getAttribute('data-nco-pin-finish');
  if (pf && filters.finish && filters.finish.length) {
    if (filters.finish.map(normFinish).indexOf(normFinish(pf)) === -1) return false;
  }
  var pt = card.getAttribute('data-nco-pin-tray');
  if (pt && filters.tray && filters.tray.length) {
    if (filters.tray.indexOf(pt) === -1) return false;
  }
  return true;
}

/* The ONE predicate for "this tile is visible under the current filters" — pagination and
   the Next button must agree, including split-variant tiles (25 tiles across 24 families). */
function visibleTileCards() {
  return Array.from(document.querySelectorAll(SEL.card)).filter(function (c) {
    var tileId = tileKey(c);
    return visibleFamilyIds.indexOf(c.getAttribute('data-nco-family-id')) !== -1 &&
      tilePinsMatch(c, lastActiveFilters) && tileEligibility[tileId] !== false;
  });
}

function applyPagination() {
  const cards = document.querySelectorAll(SEL.card);

  /* Build an ordered list of visible cards (respecting current sort).
     C1: cards are keyed by TILE id (family id, or family::variant for a curated variant split);
     the filter engine still resolves per family, and a tile's pinned facets must also agree
     with the active finish/tray selection so split tiles filter independently. */
  const sortedCards = visibleTileCards();

  sortCardArray(sortedCards);
  const totalVisible = sortedCards.length;
  const totalPages = Math.max(1, Math.ceil(totalVisible / perPage));
  if (currentPage > totalPages) currentPage = totalPages;

  /* Determine which cards belong to the current page */
  const startIdx = (currentPage - 1) * perPage;
  const endIdx = startIdx + perPage;
  const pageIds = {};
  for (let i = startIdx; i < Math.min(endIdx, sortedCards.length); i++) {
    pageIds[tileKey(sortedCards[i])] = true;
  }

  /* Show/hide cards */
  for (let i = 0; i < cards.length; i++) {
    const show = !!pageIds[tileKey(cards[i])];
    cards[i].hidden = !show;
  }

  /* Update counts (tiles, not families — identical in un-curated mode) */
  const countStr = String(sortedCards.length);
  const countEls = document.querySelectorAll(SEL.visibleCount);
  for (let j = 0; j < countEls.length; j++) countEls[j].textContent = countStr;
  const overlayEls = document.querySelectorAll(SEL.overlayCount);
  for (let k = 0; k < overlayEls.length; k++) overlayEls[k].textContent = countStr;

  /* Update pagination controls */
  const prevBtn = document.querySelector(SEL.pagePrev);
  const nextBtn = document.querySelector(SEL.pageNext);
  const pageInfoEl = document.querySelector(SEL.pageInfo);
  const paginationNav = document.querySelector(SEL.pagination);

  if (paginationNav) {
    paginationNav.hidden = totalPages <= 1;
  }
  if (prevBtn) prevBtn.disabled = currentPage <= 1;
  if (nextBtn) nextBtn.disabled = currentPage >= totalPages;
  if (pageInfoEl) {
    pageInfoEl.textContent = 'Page ' + currentPage + ' of ' + totalPages;
  }

  sortCards();
}

/* ------------------------------------------------------------------ */
/* SEO: inject rel prev / next into <head>                             */
/* ------------------------------------------------------------------ */

function injectPaginationLinks() {
  const prev = document.querySelector(SEL.prevLink);
  const next = document.querySelector(SEL.nextLink);

  if (prev) {
    const link = document.createElement('link');
    link.rel = 'prev';
    link.href = prev.href;
    document.head.appendChild(link);
  }

  if (next) {
    const link = document.createElement('link');
    link.rel = 'next';
    link.href = next.href;
    document.head.appendChild(link);
  }
}

/* ------------------------------------------------------------------ */
/* Init                                                                */
/* ------------------------------------------------------------------ */

function init() {
  const collection = document.querySelector(SEL.collection);
  if (!collection) return;

  perPage = Number(collection.getAttribute('data-nco-per-page')) || 24;

  const filterToggle = collection.querySelector(SEL.filterToggle);
  if (filterToggle) {
    filterToggle.addEventListener('click', openOverlay);
  }

  const filterClose = collection.querySelector(SEL.filterClose);
  if (filterClose) {
    filterClose.addEventListener('click', closeOverlay);
  }

  const filterApply = collection.querySelector(SEL.filterApply);
  if (filterApply) {
    filterApply.addEventListener('click', closeOverlay);
  }

  const backdrop = collection.querySelector(SEL.backdrop);
  if (backdrop) {
    backdrop.addEventListener('click', closeOverlay);
  }

  const sortSelect = collection.querySelector(SEL.sort);
  if (sortSelect) {
    sortSelect.addEventListener('change', function () {
      currentSort = sortSelect.value;
      currentPage = 1;
      applyPagination();
    });
  }

  const initPrevBtn = collection.querySelector(SEL.pagePrev);
  if (initPrevBtn) {
    initPrevBtn.addEventListener('click', function () {
      if (currentPage > 1) {
        currentPage--;
        applyPagination();
        collection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  const initNextBtn = collection.querySelector(SEL.pageNext);
  if (initNextBtn) {
    initNextBtn.addEventListener('click', function () {
      const totalPages = Math.max(1, Math.ceil(visibleTileCards().length / perPage));
      if (currentPage < totalPages) {
        currentPage++;
        applyPagination();
        collection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  document.addEventListener('nco:filter:change', function (e) {
    const detail = e.detail;
    if (detail && detail.visibleFamilyIds) {
      updateGridVisibility(detail.visibleFamilyIds, detail);
    }
  });

  /* The filter engine and this controller are both deferred module scripts. On
     some Shopify preview loads the engine's initial event can fire before this
     listener is attached. Consume the explicit initial snapshot and retry once
     after the current module queue so first paint cannot skip pagination. */
  var syncInitialState = function () {
    if (typeof window !== 'undefined' && window.ncoFilterInitialState) {
      updateGridVisibility(
        window.ncoFilterInitialState.visibleFamilyIds || [],
        window.ncoFilterInitialState,
      );
      return;
    }
    if (!visibleFamilyIds.length) {
      visibleFamilyIds = Array.from(document.querySelectorAll(SEL.card)).map(function (card) {
        return card.getAttribute('data-nco-family-id');
      });
      applyPagination();
    }
  };
  syncInitialState();
  setTimeout(syncInitialState, 0);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      const sidebar = document.querySelector(SEL.sidebar);
      if (sidebar && sidebar.classList.contains(CSS_OPEN)) {
        closeOverlay();
      }
    }
  });

  injectPaginationLinks();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
